import { store } from '@/core/store'

export const KEY_RATE_PLAYLIST = 'rate_playlist'
export const KEY_APP_USER_FEEDBACK = 'app_user_feedback'
export const KEY_REQUEST_MODIFICATIONS = 'request_modifications'
export const KEY_EDIT_PLAYLIST = 'edit_playlist'
export const KEY_REPORT_INVOICE = 'report_invoice'
export const KEY_INTEGRATIONS = 'integrations'

export interface TypeformConfig {
	id: string
	key: string
	language: string
	type: string
}

export const typeformConfigs: TypeformConfig[] = [
	{
		id: 'P9bPs0r4',
		key: KEY_RATE_PLAYLIST,
		language: 'en',
		type: 'brandtrack',
	},
	{
		id: 'WLbTSXht',
		key: KEY_RATE_PLAYLIST,
		language: 'es',
		type: 'brandtrack',
	},
	{
		id: 'e9RRTM6W',
		key: KEY_RATE_PLAYLIST,
		language: 'en',
		type: 'musicbrand',
	},
	{
		id: 'gthM7xe2',
		key: KEY_RATE_PLAYLIST,
		language: 'es',
		type: 'musicbrand',
	},
	// App user feedback
	{
		id: 'B7z3H9ca',
		key: KEY_APP_USER_FEEDBACK,
		language: 'en',
		type: 'brandtrack',
	},
	{
		id: 'vy9TW85n',
		key: KEY_APP_USER_FEEDBACK,
		language: 'es',
		type: 'brandtrack',
	},
	{
		id: 'Pj8KzJ0r',
		key: KEY_APP_USER_FEEDBACK,
		language: 'en',
		type: 'musicbrand',
	},
	{
		id: 'ZD8PzmOc',
		key: KEY_APP_USER_FEEDBACK,
		language: 'es',
		type: 'musicbrand',
	},
	// Edit playlist
	{
		id: 'pBdDaKZ5',
		key: KEY_EDIT_PLAYLIST,
		language: 'en',
		type: 'brandtrack',
	},
	{
		id: 'LIFbnaCU',
		key: KEY_EDIT_PLAYLIST,
		language: 'es',
		type: 'brandtrack',
	},
	{
		id: 'I1Ql5ER9',
		key: KEY_EDIT_PLAYLIST,
		language: 'en',
		type: 'musicbrand',
	},
	{
		id: 'tfRPG7kb',
		key: KEY_EDIT_PLAYLIST,
		language: 'es',
		type: 'musicbrand',
	},
	// Report invoice
	{
		id: 'vLPagARH',
		key: KEY_REPORT_INVOICE,
		language: 'en',
		type: 'brandtrack',
	},
	{
		id: 'CdWNdGxy',
		key: KEY_REPORT_INVOICE,
		language: 'es',
		type: 'brandtrack',
	},
	// Integrations
	{
		id: 'fXiH1ulj',
		key: KEY_INTEGRATIONS,
		language: 'en',
		type: 'brandtrack',
	},
	{
		id: 'oVRrrLI9',
		key: KEY_INTEGRATIONS,
		language: 'es',
		type: 'brandtrack',
	},
]

export interface TypeformParams {
	account_id?: string | number
	account_name?: string
	account_country?: string
	user_email?: string
	[key: string]: any
}

export const getTypeformUrl = (
	key: string,
	language: string,
	type: string,
	params?: TypeformParams
): string | null => {
	// Normalize language code (e.g., 'en-US' -> 'en', 'es-ES' -> 'es')
	const normalizedLanguage = language?.split('-')[0]?.toLowerCase() || 'en'

	// Normalize type to lowercase for case-insensitive matching
	const normalizedType = type?.toLowerCase() || 'brandtrack'

	let config: TypeformConfig | undefined

	// Handle brandtrack type specifically
	if (normalizedType === 'brandtrack') {
		// First try: exact match with key, language, and brandtrack type
		config = typeformConfigs.find(
			(c) =>
				c.key === key &&
				c.language === normalizedLanguage &&
				c.type.toLowerCase() === 'brandtrack'
		)

		// Fallback: Try with 'en' language if no match found
		if (!config && normalizedLanguage !== 'en') {
			config = typeformConfigs.find(
				(c) =>
					c.key === key &&
					c.language === 'en' &&
					c.type.toLowerCase() === 'brandtrack'
			)
		}
	}

	if (!config) {
		// First try: exact match with key, language, and type
		config = typeformConfigs.find(
			(c) =>
				c.key === key &&
				c.language === normalizedLanguage &&
				c.type.toLowerCase() === normalizedType
		)

		// Fallback 1: Try with 'brandtrack' as default type if no match found
		if (!config && normalizedType !== 'brandtrack') {
			config = typeformConfigs.find(
				(c) =>
					c.key === key &&
					c.language === normalizedLanguage &&
					c.type.toLowerCase() === 'brandtrack'
			)
		}

		// Fallback 2: Try with 'en' language if no match found
		if (!config && normalizedLanguage !== 'en') {
			config = typeformConfigs.find(
				(c) =>
					c.key === key &&
					c.language === 'en' &&
					c.type.toLowerCase() === normalizedType
			)
		}

		// Fallback 3: Try with both defaults (en + brandtrack)
		if (!config) {
			console.log('Trying fallback with en language and brandtrack type')
			config = typeformConfigs.find(
				(c) =>
					c.key === key &&
					c.language === 'en' &&
					c.type.toLowerCase() === 'brandtrack'
			)
		}
	}

	if (!config) {
		console.error('No typeform config found after all fallbacks:', {
			key,
			language,
			normalizedLanguage,
			type,
			normalizedType,
			availableConfigs: typeformConfigs.filter((c) => c.key === key),
		})
		return null
	}

	let url = `https://brandtrack.typeform.com/to/${config.id}`

	// Get user info from store
	const state = store.getState()
	const user = state.auth.user

	const defaultParams: TypeformParams = {
		account_id: user?.account?.id,
		account_name: user?.account?.name,
		account_country: user?.account?.country || undefined,
		user_email: user?.email,
	}

	// Merge default params with provided params, giving priority to provided params
	const finalParams = { ...defaultParams, ...params }

	const queryParams = new URLSearchParams()
	Object.entries(finalParams).forEach(([key, value]) => {
		if (value !== undefined && value !== null) {
			queryParams.append(key, String(value))
		}
	})

	const queryString = queryParams.toString()
	if (queryString) {
		url += `?${queryString}`
	}

	return url
}
